/**
 * VaultAttachments — the Vault as a knowledge source inside the agent and skill
 * editors, sitting beside "upload from this computer" and "data bundles".
 *
 * An uploaded file is a COPY: change it later and the agent still holds the old
 * bytes. A Vault link is a reference — the folder keeps its own contents, so
 * files added to it afterwards reach the agent with nobody re-attaching
 * anything. That difference is the reason this panel exists, and it is what the
 * hint under the title says.
 *
 * It borrows the surrounding modals' bundle-card classes deliberately: both
 * BotModal.css and SkillModal.css define them, so the Vault list looks like the
 * section above it rather than like a component someone bolted on.
 */

import { useCallback, useEffect, useState } from 'react';
import { message } from 'antd';
import { FilePreviewModal } from '@/components/common';
import { useLanguage } from '@/hooks/useLanguage';
import { useFeature } from '@/hooks/useFeature';
import { vaultService } from '../vaultService';
import type { VaultAgentLink } from '../types';
import { VaultPickerModal } from './VaultPickerModal';
import type { VaultPick } from './VaultPickerModal';
import { useNavigate } from 'react-router-dom';
import { useTenantPath } from '@/routing';
import './VaultAttachments.css';

interface Props {
  /** Exactly one: whose knowledge this is. A tenant is a subject in its own
   *  right — the organization-wide context every agent in it reads. */
  subject: { chatbot_id?: string; skill_id?: string; subject_tenant_id?: string };
  /** Class for the add button, so it matches the host modal's other buttons. */
  addButtonClassName?: string;
  disabled?: boolean;
  onChanged?: () => void;
  /** Where a linked file's preview fetches its bytes, when the host surface
   *  has its own door (the tenant tab: its organization-context route).
   *  Absent, the general file route answers, as for agents and skills. */
  previewUrlFor?: (fileId: string) => string;
}

const FolderGlyph = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round">
    <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
  </svg>
);

const FileGlyph = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round">
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
    <path d="M14 3v5h5" />
  </svg>
);

export function VaultAttachments({ subject, addButtonClassName = 'bundle-select-btn', disabled, onChanged, previewUrlFor }: Props) {
  const { t } = useLanguage();
  const [previewFile, setPreviewFile] = useState<
    { fileId?: string; fileName: string; fileType?: string; url?: string } | null
  >(null);
  // The whole section exists only where the Library module is on: with it
  // off, the tenant works purely on legacy data bundles, and showing a picker
  // whose links nothing reads would be a lie.
  const libraryOn = useFeature('modules.library');

  const navigate = useNavigate();
  const tenantPath = useTenantPath();
  const [links, setLinks] = useState<VaultAgentLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [picking, setPicking] = useState(false);
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());
  // A regular user can hold an agent without managing the Vault. Hide the whole
  // section for them rather than showing a button that only ever 403s.
  const [permitted, setPermitted] = useState(true);

  const subjectId = subject.chatbot_id || subject.skill_id || subject.subject_tenant_id;

  const load = useCallback(async () => {
    if (!subjectId) return;
    setLoading(true);
    try {
      setLinks(await vaultService.listAgentLinks(subject));
      setPermitted(true);
    } catch (e: any) {
      if (e?.response?.status === 403) setPermitted(false);
      else message.error(e?.response?.data?.error || t('vault.attach.loadFailed'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subjectId]);

  useEffect(() => {
    load();
  }, [load]);

  const attachedIds = new Set(
    links.map((l) => l.folder_id || l.file_id).filter(Boolean) as string[],
  );

  const handlePick = async (pick: VaultPick) => {
    try {
      await vaultService.createAgentLink({
        ...(pick.folder_id ? { folder_id: pick.folder_id } : { file_id: pick.file_id }),
        ...subject,
        access_mode: 'read',
        include_as_context: pick.include_as_context,
      });
      message.success(t('vault.attach.added', { name: pick.name }));
      setPicking(false);
      await load();
      onChanged?.();
    } catch (e: any) {
      message.error(e?.response?.data?.error || t('vault.attach.addFailed'));
    }
  };

  const mark = (id: string, on: boolean) =>
    setBusyIds((prev) => {
      const s = new Set(prev);
      if (on) s.add(id); else s.delete(id);
      return s;
    });

  const handleToggleContext = async (link: VaultAgentLink) => {
    const next = !link.include_as_context;
    mark(link.link_id, true);
    try {
      await vaultService.updateAgentLink(link.link_id, { include_as_context: next });
      setLinks((prev) =>
        prev.map((l) => (l.link_id === link.link_id ? { ...l, include_as_context: next } : l)),
      );
      onChanged?.();
    } catch (e: any) {
      message.error(e?.response?.data?.error || t('vault.attach.updateFailed'));
    } finally {
      mark(link.link_id, false);
    }
  };

  const handleRemove = async (link: VaultAgentLink) => {
    mark(link.link_id, true);
    try {
      await vaultService.deleteAgentLink(link.link_id);
      setLinks((prev) => prev.filter((l) => l.link_id !== link.link_id));
      onChanged?.();
    } catch (e: any) {
      message.error(e?.response?.data?.error || t('vault.attach.removeFailed'));
    } finally {
      mark(link.link_id, false);
    }
  };

  if (!libraryOn || !subjectId || !permitted) return null;

  return (
    <div className="form-group vault-attach-section">
      <label className="section-label">{t('vault.attach.title')}</label>
      <p className="section-description">{t('vault.attach.description')}</p>

      {/* The flag decides whether the agent's file list is built from the Vault
          or from the legacy tables. With it off the link is stored and shown but
          nothing reads it — say so instead of letting someone believe it works. */}

      {links.length > 0 && (
        <div className="bundle-selected-list">
          {links.map((l) => (
            <div key={l.link_id} className="bundle-selected-card">
              <div className="bundle-selected-icon">
                {l.node_type === 'folder' ? <FolderGlyph /> : <FileGlyph />}
              </div>
              {/* A linked FILE opens where you click it, the same as an
                  uploaded one right below — a name you cannot open is a dead
                  end, and there was no other way to see what had been linked.
                  A folder is not a document, so it stays plain text. */}
              <div
                className={`bundle-selected-info${l.node_type === 'file' ? ' is-openable' : ''}`}
                onClick={l.node_type === 'file' && l.file_id
                  ? () => setPreviewFile({
                    fileId: l.file_id,
                    fileName: l.node_name || t('vault.attach.aFile'),
                    fileType: '',
                    url: previewUrlFor?.(l.file_id!),
                  })
                  : undefined}
                title={l.node_type === 'file' ? t('filePreview.preview') : undefined}
              >
                <div className="bundle-selected-name" dir="auto">
                  {l.node_name || t(l.node_type === 'folder' ? 'vault.attach.aFolder' : 'vault.attach.aFile')}
                  {/* The reorg dot: this link (or its folder) was made by the
                      bundles migration — a candidate for replacement by its
                      copy in the new structure (operator request 2026-09-08). */}
                  {l.migrated && (
                    <span
                      className="vault-mig-dot"
                      title={t(`vault.attach.migrated.${l.migrated_kind || 'bundle_folder'}`)}
                    />
                  )}
                </div>
                {/* The node's full Library location, clickable: jumping to the
                    folder is how a reorganizer proves a link points where they
                    think it does. Permission still rules — the Library page
                    itself decides what opens. */}
                {(l.path?.length ?? 0) > 0 && (
                  <div
                    className={`bundle-selected-path${l.can_open ? '' : ' no-access'}`}
                    onClick={l.can_open ? (e) => {
                      e.stopPropagation();
                      const last = l.path![l.path!.length - 1];
                      navigate(`${tenantPath('/library')}?folder=${last.folder_id}`);
                    } : undefined}
                    title={l.can_open
                      ? t('vault.attach.openInLibrary')
                      : (l.owner_name
                        ? t('vault.attach.noAccessOwnedBy', { owner: l.owner_name })
                        : t('vault.attach.noAccessToOpen'))}
                  >
                    {l.path!.map((p) => p.name).join(' / ')}
                  </div>
                )}
                <div className="bundle-selected-meta">
                  {t(l.node_type === 'folder' ? 'vault.attach.folderMeta' : 'vault.attach.fileMeta')}
                </div>
              </div>
              <div className="bundle-context-toggle" title={t('vault.attach.contextTooltip')}>
                <span className="context-toggle-label">{t('vault.attach.alwaysInContext')}</span>
                <label className="context-switch" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={l.include_as_context}
                    onChange={() => handleToggleContext(l)}
                    disabled={busyIds.has(l.link_id) || disabled}
                  />
                  <span className="context-switch-slider"></span>
                </label>
              </div>
              <button
                type="button"
                className="bundle-remove-btn"
                onClick={() => handleRemove(l)}
                disabled={busyIds.has(l.link_id) || disabled}
                title={t('vault.attach.remove')}
                aria-label={t('vault.attach.remove')}
              >
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

      <button
        type="button"
        className={addButtonClassName}
        onClick={() => setPicking(true)}
        disabled={disabled || loading}
      >
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="12" y1="5" x2="12" y2="19"></line>
          <line x1="5" y1="12" x2="19" y2="12"></line>
        </svg>
        {t('vault.attach.add')}
      </button>

      <VaultPickerModal
        open={picking}
        attached={attachedIds}
        onPick={handlePick}
        onCancel={() => setPicking(false)}
      />

      {/* The same viewer the uploaded files below use, so a linked file and an
          uploaded one open the same way. */}
      <FilePreviewModal
        visible={!!previewFile}
        fileId={previewFile?.fileId}
        fileName={previewFile?.fileName || ''}
        fileType={previewFile?.fileType}
        downloadUrl={previewFile?.url}
        onClose={() => setPreviewFile(null)}
      />
    </div>
  );
}
